import { authAPI, getGlobalDataWithHeader, multiSiteApi } from "@platformx/authoring-apis";
import { handleUpdateRoutes, RootState } from "@platformx/authoring-state";
import { graphQluserRole } from "@platformx/user-role";
import {
  AUTH_INFO,
  AUTH_URL,
  getSelectedSite,
  setSelectedBrandDetails,
  usePlatformAnalytics,
  useUserSession,
} from "@platformx/utilities";
import { useDispatch, useSelector } from "react-redux";
import { existingRoutes } from "../router/existingRoutes";
import { fetchRoutes } from "../router/fetchRoutes";
import { createSession } from "../utils/helper";

export const useAuthentication = () => {
  const [handleImpression] = usePlatformAnalytics();
  const [getSession, updateSession] = useUserSession();
  const dispatch = useDispatch();
  const { routes = [] } = useSelector((state: RootState) => state?.routes);

  const fetchDynamicBrands = async (): Promise<{ brandId: string; brandName: string }[]> => {
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      return await authAPI.fetchBrandDetails();
    } catch (error) {
      console.error("Error fetching brand details:", error);
      return [];
    }
  };

  const fetchDynamicRoutes = async () => {
    if (!routes?.length) {
      const dynamicRoutesList = await fetchRoutes();
      const usersession = JSON.parse(localStorage.getItem("userSession") || "") || {};
      const allRoutes = [...existingRoutes, ...dynamicRoutesList];
      // IF super admin, hide all routes except Sites
      if (usersession?.role === "Super Admin") {
        const updatedAllroutes = allRoutes.map((route) => {
          if (route?.id === "Sites" || route?.category === "public") {
            return route;
          } else {
            return {
              ...route,
              isMenu: false,
              isHidden: true,
              isSideBar: false,
              isHeader: false,
              isGallery: false,
              hasSearch: false,
            };
          }
        });
        dispatch(handleUpdateRoutes(updatedAllroutes));
      }
      //For all other roles, update hide Sites.
      else {
        const { data: { authoring_rolePermissionList = [] } = {} } =
          (await graphQluserRole.getRolePermissionList()) || {};
        const adminRolesAccess =
          authoring_rolePermissionList?.find((item) => {
            return item?.role?.toLowerCase() === "admin";
          })?.permissions || [];
        localStorage.setItem("adminRolesAccess", JSON.stringify(adminRolesAccess));
        // Update the routes based on admin roles
        const updatedAllroutes = allRoutes.map((route) => {
          const doesSiteHasModuleAccess = adminRolesAccess?.some(
            (item) =>
              item?.category?.toLowerCase() === route?.category?.toLowerCase() &&
              item?.sub_category?.toLowerCase() === route?.subCategory?.toLowerCase(),
          );
          if (route?.category === "public") {
            return route;
          } else if (route?.id === "Sites") {
            return {
              ...route,
              isMenu: false,
              isHidden: true,
              isSideBar: false,
              isHeader: false,
              isGallery: false,
              hasSearch: false,
            };
          }
          // If the route is not part of site access then we hide it.
          else if (!doesSiteHasModuleAccess) {
            return {
              ...route,
              isMenu: false,
              isHidden: true,
              isSideBar: false,
              isHeader: false,
              isGallery: false,
              hasSearch: false,
            };
          } else {
            return route;
          }
        });
        dispatch(handleUpdateRoutes(updatedAllroutes));
      }
    }
  };

  const handleSignIn = async (authCode) => {
    const payload = {
      code: authCode,
      client_id: AUTH_INFO.clientId,
      grant_type: AUTH_INFO.grantType,
      redirect_uri: AUTH_INFO.redirectUri,
      tenant_id: AUTH_INFO.realm,
    };

    try {
      const response = await authAPI.signIn("auth/session", payload);

      if (response?.data) {
        const userDetails = { ...response.data, isActive: "true" };
        const { roles, selected_site } = response.data;
        localStorage.setItem("selectedSite", selected_site);

        fetchDynamicRoutes();
        // Determine user role based on selected site
        const userRole = roles?.find(
          (obj) => obj.site?.toLowerCase() === selected_site?.toLowerCase(),
        )?.name;

        updateSession(
          createSession(
            {
              ...response.data,
            },
            true,
            userRole,
          ),
        );

        // Send login user info to Analytics End
        handleImpression(userDetails.eventType, userDetails);

        // Fetch global data if the selected site is not 'system'
        if (selected_site?.toLowerCase() !== "system") {
          await getGlobalDataWithHeader(selected_site);
        }

        // Update session with user data
        const dynamicBrands = await fetchDynamicBrands();

        // Set the first dynamic brand as selected by default after login
        if (dynamicBrands?.length > 0) {
          const [firstBrand] = dynamicBrands;
          setSelectedBrandDetails(firstBrand.brandId, firstBrand.brandName);
        }

        // Determine language preference
        const lang = response.data.preferred_sites_languages?.[selected_site] || "en";

        // Determine the redirect path based on the selected site
        const redirectPath =
          selected_site?.toLowerCase() === "system" ? `sites/site-listing` : `dashboard`;

        // Redirect the user to the appropriate path
        window.location.replace(
          `${process.env.NX_PUBLIC_BASE_URL}/${selected_site}/${lang}/${redirectPath}`,
        );
      }
    } catch (error: unknown) {
      // Handle sign-in errors
      console.error("Error signing in:", error);
    }
  };

  const verifySession = async () => {
    try {
      // Send session verification request
      const response = await authAPI.verifySession("auth/verify-session");
      if (response?.data) {
        fetchDynamicRoutes();
        const { active } = response.data || { userDetails: {} };

        const currentSelectedSite = getSelectedSite();
        const storedSelectedSite = localStorage.getItem("selectedSite");

        if (currentSelectedSite === storedSelectedSite) {
          // Update session with active status
          updateSession({
            ...getSession(),
            isActive: active || false,
          });
        } else {
          // Update selected site in local storage
          localStorage.setItem("selectedSite", currentSelectedSite);
          // Fetch permissions for the current selected site
          const res = await multiSiteApi.getPermissions(currentSelectedSite);

          // Update session with new permissions and user info
          const dynamicBrands = await fetchDynamicBrands();
          updateSession({
            ...getSession(),
            isActive: active || false,
            permissions: res.data?.data?.permissions,
            userInfo: res.data?.data,
            multi_brands: dynamicBrands,
            role: res.data?.data?.roles?.find(
              (obj) => obj.site?.toLowerCase() === res.data?.data?.selected_site?.toLowerCase(),
            )?.name,
          });

          if (dynamicBrands.length > 0) {
            const [firstBrand] = dynamicBrands;
            setSelectedBrandDetails(firstBrand.brandId, firstBrand.brandName);
          }

          const isSystemSite = currentSelectedSite.toLowerCase() === "system";

          // Always refresh the site's DAM community mapping when the selected
          // site changed. A previous guard skipped this whenever any uuid was
          // already present, so a multi-site editor kept the prior site's DAM
          // mapping after switching sites.
          if (!isSystemSite) {
            await getGlobalDataWithHeader(currentSelectedSite);
          }
        }
      } else {
        // Clear session and redirect to authentication URL
        updateSession(null);
        window.location.replace(AUTH_URL);
      }
    } catch (error) {
      // Handle errors as needed
      console.error("Error verifying session:", error);
    }
  };

  return { handleSignIn, verifySession };
};
